import { ApplicationRef, Component, HostListener, OnInit } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { LocalStorageService } from './shared/local-storage.service';
import { LookupDataService } from './shared/lookup-data.service';
import { AuthService } from './shared/auth.service';
import { catchError, first, timeout } from 'rxjs/operators';
import { ActivatedRoute, Router, RoutesRecognized } from '@angular/router';
import { OnlineStatusService, OnlineStatusType } from 'ngx-online-status';
import { ToastrService } from 'ngx-toastr';
import { OperatorUser } from './shared/operator-user.model';
import { throwError } from 'rxjs';
import shpjs from 'shpjs';
import { Insights } from './shared/insights.service';
import { ConflictResolverService } from './shared/conflict-resolver.service';

/**
 * App/Root component
 */
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    standalone: false
})
export class AppComponent implements OnInit {
  title = 'psmfc-elog';
  displayComponent: boolean = false;

  private readonly REQUEST_TIMEOUT_MS = 3 * 1000;
  /**
   * In the app component constructor, wait for the app to stabilize then check for and apply updates. 
   * @param updates Angular service to check for new versions of the app
   * @param appRef To check app stability before applying updates
   */
  constructor(
    public dataSync: LocalStorageService,
    private auth: AuthService,
     private router: Router,
     private toastr: ToastrService,
      public route: ActivatedRoute,
      private onlineStatusService: OnlineStatusService,
       private lookup: LookupDataService,
        updates: SwUpdate,
         private appRef: ApplicationRef,
        private insights: Insights) {
      // Allow the app to stabilize first, before checking for an update
      const appIsStable$ = this.appRef.isStable.pipe(first(isStable => isStable === true));
      console.log('Waiting for app stabilization...');

      updates.unrecoverable.subscribe(event => {
        console.log(event.reason);
        document.location.reload();
      });

      updates.versionUpdates.subscribe(evt => {     
        switch (evt.type) {
          case 'VERSION_DETECTED':
            this.toastr.success('New app version found, please wait for reload...');
            console.log(`Downloading new app version: ${evt.version.hash}`);
            break;
          case 'VERSION_READY':
            console.log(`Current app version: ${evt.currentVersion.hash}`);
            console.log(`New app version ready for use: ${evt.latestVersion.hash}`);
            break;
          case 'VERSION_INSTALLATION_FAILED':
            console.log(`Failed to install app version '${evt.version.hash}': ${evt.error}`);
            break;
        } 
      });

      appIsStable$.subscribe({complete: () => {
        console.log('App stable, checking for update...');
        
        const updateFound = updates.checkForUpdate();
        updateFound.then((update: any) =>{
          if (update) {
            this.toastr.success('New version found, reloading...');
            document.location.reload();
          } else {
            console.log('Application up-to-date');
          }
        }).catch(_ => { console.log('%c Cannot check for updates. running in localhost or service workers are disabled or not supported', 
                                      'color: antiquewhite; font-weight: bold; background-color: black;');  });   
         }, error: (err) => { console.log("Error in waiting for stabilization"); console.log(err); }});

      router.events.subscribe(event => {
        if (event instanceof RoutesRecognized) {
          if (this.dataSync.user_storage && 
            this.onlineStatusService.getStatus() == OnlineStatusType.ONLINE &&
            this.dataSync.logged_in()) this.dataSync.syncUserDatabase(true);
        }
      });

      ConflictResolverService.insights = this.insights;
    }


    @HostListener('document:visibilitychange', ['$event'])
    visibilitychange() {
      if (this.dataSync.user_storage && 
        this.dataSync.logged_in() && 
        this.onlineStatusService.getStatus() == OnlineStatusType.ONLINE) this.dataSync.syncUserDatabase(true);
    }


    /**
     * The 'first' thing to run whenever the app loads or is refreshed.
     * Check if a user is still logged in and perform the login sequence if they are.
     */
  ngOnInit(): void {
    console.log('Init app');
    
    //We check the login_status document in storage for a still logged in user
    this.dataSync.openUserAccounts().then(() => {
      //If logged in, refresh access token and retrieve all data from DB
      //If refresh token has expired then logout and go to login screen
      this.insights.trackEvent('Open accounts', 'Accounts opened on app init');

      if (this.dataSync.logged_in()) {
        this.insights.trackEvent('Auto login', 'User already logged in');
        if (this.onlineStatusService.getStatus() === OnlineStatusType.ONLINE && this.auth.checkRefreshToken().expired) {
          console.log('Refresh token expired');
          this.toastr.warning('Login session has expired. Log in again to refresh', 'Session Expired')
          this.dataSync.logged_in.set(false);
          this.dataSync.local_storage.get('login_status').then((doc: any) => {
            doc.user = '';
            doc.isLoggedIn = false;
            this.dataSync.local_storage.put(doc);
            this.router.navigate(['login']);
            this.displayComponent = true;
          });
          this.dataSync.cleanupStorage();         
        } else {
          //Refresh token is not expired, user is offline, or both
          if (this.onlineStatusService.getStatus() === OnlineStatusType.ONLINE) {
            //Refresh token has not expired            

            //Check for a migration, open storage, then sync
            this.dataSync.checkForMigration().then((res: boolean) => {

              if (!res) {
                //Need to perform a migration
                this.insights.trackEvent('HMS Migration', 'Need to migrate to HMS');
                this.dataSync.migrateToHMSCombined().then(() => {
                //We need to get this user's couchDB connection, so try refreshing the token first
                this.auth.postRefreshToken().subscribe(result => {
                  console.log("Token refresh for couchDB account creation");
                    this.dataSync.openLocalStorage();
                    this.dataSync.storage_promise.then(() => {
                      this.dataSync.syncUserDatabase();
                      this.displayComponent = true;
                      this.dataSync.sync_promise.then(() => this.afterMigrationCheck());
                    }).catch(_ => {
                      this.displayComponent = true;
                      this.logoutUser();
                    });  
                  });                 
                }, _ => {
                  this.displayComponent = true;
                  this.logoutUser()
                });
                
              } else {
                this.insights.trackEvent('App init open database', 'Initial database opening');
                this.dataSync.openLocalStorage();
                this.dataSync.storage_promise.then(() => {
                  if (this.dataSync.current_user().couchDbConnection == "") {
                    //Need to get the user couchDB connection
                    this.auth.postRefreshToken().subscribe({
                      next: (response) => {
                      console.log("Token refresh for couchDB account creation");
                      this.dataSync.current_user.update(user => {user.couchDbConnection = response.couchDbConnection; return user});                      
                      this.displayComponent = true;
                      this.afterMigrationCheck(); 
                      this.dataSync.syncUserDatabase(true);
                      }, 
                      error: () => this.logoutUser()});
                  } else {   
                    this.displayComponent = true;
                    this.afterMigrationCheck();
                    this.dataSync.syncUserDatabase(true);
                  }   
                });
                                                      
              }
            })          
         
          } else {
            //User is not online and might have expired refresh token      
            this.dataSync.openLocalStorage();
            this.dataSync.storage_promise.then(() => {
              this.displayComponent = true;
              this.lookup.retrieveLookupData();
              if (this.router.url == '/login') this.router.navigate(['menu']);
            }).catch(() => {
              console.log('Error in offline open user storage');
              this.lookup.retrieveLookupData();
              if (this.router.url == '/login') this.router.navigate(['menu']);
            })
                       
          }
        }
        
      } else {
        this.displayComponent = true;
        console.log('User is not logged in');
        this.router.navigate(['login']);
      }
    });

    fetch("../../assets/EEZ_WestCoast_PacVes_shapefile.zip")
                .then(text => text.arrayBuffer())
                .then((myshape) => shpjs(myshape))
                .then((data: any) => {
                        this.dataSync.west_coast_eez.set(data.features[0].geometry);
                }).catch(e => {
                  this.insights.trackException(e);
                  this.insights.trackTrace("Failed to retrieve the shapefile");
                });
  }


  /**
   * Set the tokens and retrieve lookup data the go to the main menu.
   */
  afterMigrationCheck() {
    if (this.router.url == '/login') this.router.navigate(['menu']);
    if (this.auth.checkAccessToken().expired) {
      this.auth.postRefreshToken().pipe(timeout(this.REQUEST_TIMEOUT_MS), catchError((error) => {
        if (error.name === 'TimeoutError') console.error('Request timed out.');
        return throwError(error);
      }))   
      .subscribe(res => {
        this.dataSync.current_user.update(user => {
          user.refreshToken = res.refreshToken;
          user.accessToken = res.accessToken;
          return user;
        });
        this.dataSync.accessToken.set(res.accessToken);
        this.dataSync.updateUser(false, true);
        this.lookup.retrieveLookupData();
      }, _ => this.lookup.retrieveLookupData());
    } else {
      this.lookup.retrieveLookupData();     
    }   
  }


  /**
   * Update login status and go back to login.
   */
  logoutUser() {
    //If error then we have to log the user out   
    this.insights.trackEvent('Logging out user', 'Logging out the current user');               
    this.dataSync.logged_in.set(false);
    this.dataSync.local_storage.upsert('login_status', (doc: any) => {
      doc.user = '';
      doc.isLoggedIn = false;
      return doc;
    }).then(() => this.insights.trackEvent('Missing logn status doc', 'Upserted login status document'))
    .catch(err => this.insights.trackException(err));

    this.dataSync.current_user.set(new OperatorUser());
    this.router.navigate(['login'])
  }
}
